import type { LogoutOptions, RedirectLoginOptions } from '@auth0/auth0-react'

import { Dispensary as DispensaryData } from '../../../graphql/search'
import { getUrlForNormalizedPath, normalizePath, stringStartsWith } from '../../pages/utils'
import { dispensaryBasePathPrefix, dispensaryLogoutPath } from '../config'
import { Dispensary } from '../types'

export const getDispensaryBasePath = (slug: string): string => {
  return `/${dispensaryBasePathPrefix}${normalizePath(slug)}`
}

/**
 * Add dynamic segment to router base path (if within dispensary) to support case-insensitive slugs
 */
export const getRouterBasePath: (path: string, isDispensary: boolean) => string = (
  path,
  isDispensary,
) =>
  isDispensary ? `/${dispensaryBasePathPrefix}/:slug${normalizePath(path)}` : normalizePath(path)

// not exported, to ensure `addDispensaryBasePathToPath` is used instead
const getDispensaryPagePath = (dispensarySlug: string, pageSlug?: string | null): string => {
  return `${getDispensaryBasePath(dispensarySlug)}${normalizePath(pageSlug)}`
}

export const addDispensaryBasePathToPath = (
  dispensarySlug: string,
  path?: string | null,
): string => {
  const basePath = getDispensaryBasePath(dispensarySlug)
  if (!path) {
    return basePath
  }

  const normalizedPath = normalizePath(path)

  const match = normalizedPath.match(/^\/u\/([^/]+)/)
  const pathSlug = match?.[1]

  if (pathSlug && pathSlug !== dispensarySlug) {
    return normalizedPath
  }

  if (stringStartsWith(normalizedPath, basePath, { ignoreCase: true })) {
    return normalizedPath
  }
  return getDispensaryPagePath(dispensarySlug, normalizedPath)
}

const captureDispensarySlugRegex = new RegExp(`^/${dispensaryBasePathPrefix}/([-\\w]+)`)

export const getDispensarySlugFromPath = (path: string): string | undefined => {
  const matches = normalizePath(path).match(captureDispensarySlugRegex)
  if (!matches) {
    return undefined
  }
  const [, dispensarySlug] = matches
  return dispensarySlug
}

const dispensaryPrefixAndSlugRegex = new RegExp(`^/${dispensaryBasePathPrefix}/[-\\w]+`)

export const removeDispensaryPrefixAndSlug = (path: string): string => {
  return path.replace(dispensaryPrefixAndSlugRegex, '')
}

const captureDispensaryPathPartsRegex = new RegExp(
  `^(/${dispensaryBasePathPrefix}/)([-\\w]+)(/.*)?`,
)

export const parseDispensaryPathParts = (
  path: string,
): {
  prefix: string
  slug: string
  restOfPath: string
} | null => {
  const matches = normalizePath(path).match(captureDispensaryPathPartsRegex)
  if (!matches) {
    return null
  }
  const [, prefix, slug, restOfPath] = matches
  return {
    prefix,
    slug,
    restOfPath: restOfPath || '',
  }
}

export const replaceDispensarySlug = (path: string, dispensarySlug: string): string => {
  return path.replace(captureDispensaryPathPartsRegex, `$1${dispensarySlug}$3`)
}

export const replaceCurrentDispensarySlug = (dispensarySlug: string): string => {
  const currentPath = window.location.href.replace(window.location.origin, '')
  return replaceDispensarySlug(currentPath, dispensarySlug)
}

export const isValidDispensaryPath = (path: string): boolean => {
  const normalizedPath = normalizePath(path)
  return !!normalizedPath && dispensaryPrefixAndSlugRegex.test(normalizedPath)
}

export const addDispensaryBasePathToUrl = (dispensarySlug: string, url?: string | null): string => {
  if (!url) {
    return getUrlForNormalizedPath('')
  }
  const { origin, pathname, search, hash } = new URL(url)
  const path = `${pathname}${search}${hash}`
  const dispensaryPath = addDispensaryBasePathToPath(dispensarySlug, path)
  return `${origin}${dispensaryPath}`
}

// note: since `%` isn't allowed, we use `@`
const encodeAuth0ParamRegex = /[^-\w.*~+ /:]/gu

/**
 * Ampersand-encode string for use as Auth0 transaction parameter
 */
export const ampEncodeAuth0TransParam = (value: string): string => {
  const matches = value.matchAll(encodeAuth0ParamRegex)
  const output = []
  // UTF-16 code unit index
  let nextIndex = 0
  const encoder = new TextEncoder()
  const buf = new Uint8Array(4)
  for (const match of matches) {
    if (match.index > nextIndex) {
      // append portion that does not need to be encoded
      output.push(value.substring(nextIndex, match.index))
    }
    // get UTF-8 bytes
    const { written } = encoder.encodeInto(match[0], buf)
    // print each byte as hex string with `@` prefix (since `%` is not allowed)
    for (let i = 0; i < written; i++) {
      output.push(`@${buf[i].toString(16).toUpperCase()}`)
    }
    // note: Unicode character may use 1 or 2 UTF-16 code units so add the match length
    nextIndex = match.index + match[0].length
  }
  if (value.length > nextIndex) {
    // append remainder of input that does not need to be encoded
    output.push(value.substring(nextIndex))
  }
  return output.join('')
}

export const mergeDispensaryLoginOptions = (
  dispensary: Dispensary,
  options?: RedirectLoginOptions,
): RedirectLoginOptions => {
  const optionsObj = options || {}
  return {
    'ext-account-type': 'customer',
    // pass dispensary home URL (e.g. for anchor tag around logo)
    'ext-home-url':
      typeof window !== 'undefined'
        ? `${window.location.origin}${getDispensaryBasePath(dispensary.slug)}`
        : '',
    // in case user proceeds to create account, pass dispensary practitioner code
    'ext-practitioner-code': ampEncodeAuth0TransParam(dispensary.practitionerCode),
    ...optionsObj,
    appState: {
      ...(optionsObj.appState || {}),
      returnTo: addDispensaryBasePathToPath(dispensary.slug, optionsObj.appState?.returnTo),
    },
  }
}

export const mergeDispensaryLogoutOptions = (
  dispensarySlug: string,
  options?: LogoutOptions,
): LogoutOptions => {
  const optionsObj = options || {}
  const dispensaryPath = getDispensaryBasePath(dispensarySlug)
  const logoutPath = normalizePath(
    `${dispensaryLogoutPath}?path=${encodeURIComponent(dispensaryPath)}`,
  )
  const logoutUrl = getUrlForNormalizedPath(logoutPath)
  return {
    ...optionsObj,
    returnTo: logoutUrl,
  }
}

export const getDispensaryFromData = (data: DispensaryData): Dispensary => {
  return {
    isInvalid: false,
    ...data,
    practitionerCodeForOrder: data.practitionerCode,
    practitionerCodeForWellWorldOrder: data.practitionerCode,
  }
}

/**
 * Remove all characters that are not alphanumeric, dashes or underscores
 */
export const sanitizeUrlSlug = (dirtyUrlSlug: string): string => {
  const regex = /[^a-zA-Z0-9_-]/g
  return (dirtyUrlSlug || '').replace(regex, '')
}
